import type { LucideIcon } from "lucide-react";
import { Blocks, CircleGauge, CreditCard, Settings } from "lucide-react";
import { create } from "zustand";
import { PersistOptions, createJSONStorage, persist } from "zustand/middleware";

export type SettingsTab = {
  id: "general" | "billing" | "usage" | "connections";
  name: string;
  icon: LucideIcon;
};

export const SETTINGS_TABS: SettingsTab[] = [
  { id: "general", name: "Settings", icon: Settings },
  { id: "billing", name: "Plan & billing", icon: CreditCard },
  { id: "usage", name: "Usage", icon: CircleGauge },
  { id: "connections", name: "Connections", icon: Blocks },
];

export type SidebarStore = {
  isUpgradeDialogOpen: boolean;
  setIsUpgradeDialogOpen: (isOpen: boolean) => void;
  isSettingsDialogOpen: boolean;
  setIsSettingsDialogOpen: (isOpen: boolean) => void;
  settingsDialogActiveTab: SettingsTab["id"];
  setSettingsDialogActiveTab: (tabId: SettingsTab["id"]) => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isOpen: boolean) => void;
  isGetExtraDialogOpen: boolean;
  setIsGetExtraDialogOpen: (isOpen: boolean) => void;
  isSearchDialogOpen: boolean;
  setIsSearchDialogOpen: (isOpen: boolean) => void;
  connectDialogOption: "drive" | "github" | null;
  setConnectDialogOption: (option: "drive" | "github" | null) => void;
};

export const useSidebarStore = create<SidebarStore>((set) => ({
  isUpgradeDialogOpen: false,
  setIsUpgradeDialogOpen: (isOpen: boolean) =>
    set({ isUpgradeDialogOpen: isOpen }),
  isSettingsDialogOpen: false,
  setIsSettingsDialogOpen: (isOpen: boolean) =>
    set({ isSettingsDialogOpen: isOpen }),
  settingsDialogActiveTab: "general",
  setSettingsDialogActiveTab: (tabId: SettingsTab["id"]) =>
    set({ settingsDialogActiveTab: tabId }),
  isDropdownOpen: false,
  setIsDropdownOpen: (isOpen: boolean) => set({ isDropdownOpen: isOpen }),
  isGetExtraDialogOpen: false,
  setIsGetExtraDialogOpen: (isOpen: boolean) =>
    set({ isGetExtraDialogOpen: isOpen }),
  isSearchDialogOpen: false,
  setIsSearchDialogOpen: (isOpen: boolean) =>
    set({ isSearchDialogOpen: isOpen }),
  connectDialogOption: null,
  setConnectDialogOption: (option: "drive" | "github" | null) =>
    set({ connectDialogOption: option }),
}));

export type CanvasStore = {
  canvasTab: "timeline" | "files" | "app" | null;
  setCanvasTab: (tab: "timeline" | "files" | "app" | null) => void;
  activeDeploymentId: string | null;
  setActiveDeploymentId: (id: string | null) => void;
  selectedFilePath: string | null;
  setSelectedFilePath: (path: string | null) => void;
  openFileInCanvas: (path: string) => void;
};

export const useCanvasStore = create<CanvasStore>((set) => ({
  canvasTab: null,
  setCanvasTab: (tab: "timeline" | "files" | "app" | null) =>
    set({ canvasTab: tab }),
  activeDeploymentId: null,
  setActiveDeploymentId: (id: string | null) => set({ activeDeploymentId: id }),
  selectedFilePath: null,
  setSelectedFilePath: (path: string | null) => set({ selectedFilePath: path }),
  openFileInCanvas: (path: string) => {
    set({ canvasTab: "files", selectedFilePath: path });
  },
}));

export type DraftPromptStore = {
  draftPrompt: string;
  setDraftPrompt: (prompt: string) => void;
  clearDraftPrompt: () => void;
};

type DraftPromptPersist = PersistOptions<
  DraftPromptStore,
  Pick<DraftPromptStore, "draftPrompt">
>;

const draftPromptPersistConfig: DraftPromptPersist = {
  name: "draft-prompt-storage",
  storage: createJSONStorage(() => localStorage),
};

export const useDraftPromptStore = create<DraftPromptStore>()(
  persist(
    (set) => ({
      draftPrompt: "",
      setDraftPrompt: (prompt) => set({ draftPrompt: prompt }),
      clearDraftPrompt: () => set({ draftPrompt: "" }),
    }),
    draftPromptPersistConfig,
  ),
);

export type StatusStore = {
  isMaintenanceMode: boolean;
  setIsMaintenanceMode: (isActive: boolean) => void;
  isHighDemand: boolean;
  setIsHighDemand: (isActive: boolean) => void;
};

export const useStatusStore = create<StatusStore>((set) => ({
  isMaintenanceMode: false,
  setIsMaintenanceMode: (isActive: boolean) =>
    set({ isMaintenanceMode: isActive }),
  isHighDemand: false,
  setIsHighDemand: (isActive: boolean) => set({ isHighDemand: isActive }),
}));

export type DeprecatedCanvasStore = {
  isCanvasOpen: boolean;
  setIsCanvasOpen: (isOpen: boolean) => void;
  activeTab: "timeline" | "files" | "app";
  setActiveTab: (tab: "timeline" | "files" | "app") => void;
  activeAssistantId: string | null;
  setActiveAssistantId: (id: string | null) => void;
  activeAppUrl: string | null;
  setActiveAppUrl: (url: string | null) => void;
  currentJamId: string | null;
  setCurrentJamId: (id: string | null) => void;
  selectedFileId: string | null;
  setSelectedFileId: (id: string | null) => void;
};

export const useDeprecatedCanvasStore = create<DeprecatedCanvasStore>(
  (set) => ({
    isCanvasOpen: false,
    setIsCanvasOpen: (isOpen: boolean) => set({ isCanvasOpen: isOpen }),
    activeTab: "timeline" as "timeline" | "files" | "app",
    setActiveTab: (tab) => set({ activeTab: tab }),
    activeAssistantId: null,
    setActiveAssistantId: (id: string | null) => set({ activeAssistantId: id }),
    activeAppUrl: null,
    setActiveAppUrl: (url: string | null) => set({ activeAppUrl: url }),
    currentJamId: null,
    setCurrentJamId: (id: string | null) => set({ currentJamId: id }),
    selectedFileId: null,
    setSelectedFileId: (id: string | null) => set({ selectedFileId: id }),
  }),
);

export type JamHeaderStore = {
  isScrolled: boolean;
  setIsScrolled: (scrolled: boolean) => void;
};

type JamHeaderPersist = PersistOptions<
  JamHeaderStore,
  Pick<JamHeaderStore, "isScrolled">
>;

const jamHeaderPersistConfig: JamHeaderPersist = {
  name: "jam-header-storage",
  storage: createJSONStorage(() => localStorage),
};

export const useJamHeaderStore = create<JamHeaderStore>()(
  persist(
    (set) => ({
      isScrolled: false,
      setIsScrolled: (scrolled) => set({ isScrolled: scrolled }),
    }),
    jamHeaderPersistConfig,
  ),
);

export type JamScrollStore = {
  showScrollButton: boolean;
  setShowScrollButton: (show: boolean) => void;
  scrollToBottom: () => void;
  setScrollToBottom: (fn: () => void) => void;
};

export const useJamScrollStore = create<JamScrollStore>((set) => ({
  showScrollButton: false,
  setShowScrollButton: (show) => set({ showScrollButton: show }),
  scrollToBottom: () => {},
  setScrollToBottom: (fn) => set({ scrollToBottom: fn }),
}));
